import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Messenger from "./components/Messenger";
import SignIn from "./Pages/SignIn";
import { AppDispatch, RootState } from "./redux/store";
import Cookies from "js-cookie";
import { readHandoffToken, redirectIfMigrated } from "./Utils/migrationHandoff";
import {
  verifyAuthWithEsChat,
  setIsAuthenticating,
} from "./redux/reducers/authSlice";
import GlobalNotification from "./components/GlobalNotification";
// import { addNotification } from "./redux/reducers/notificationSlice";
// import { NotificationIcon, NotificationType } from "./Utils/notificationEnums";

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isAuthenticating } = useSelector<RootState, { isAuthenticated: boolean; isAuthenticating: boolean }>(
    (state) => state.auth
  );

  // Check for authToken in URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const urlAuthToken = urlParams.get('authToken');

  // Show loading while authenticating
  if (isAuthenticating) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-default-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="text-body font-body text-subtext-color">
            Verifying your session...
          </span>
        </div>
      </div>
    );
  }

  // Allow access if authenticated OR if URL contains authToken
  if (!isAuthenticated && !urlAuthToken) {
    return <Navigate to="/sign-in" replace />;
  }

  return children;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isAuthenticating } = useSelector<RootState, { isAuthenticated: boolean; isAuthenticating: boolean }>(
    (state) => state.auth
  );

  // Show loading while authenticating
  if (isAuthenticating) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-default-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="text-body font-body text-subtext-color">
            Verifying your session...
          </span>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  const dispatch = useDispatch<AppDispatch>();

  // A migrated tenant must not reach this app's inbox at all, so the handoff check runs before
  // the session is verified rather than after it. Both ways in are covered here: the es_chat
  // cookie, and the authToken URL parameter that ProtectedRoute accepts with no Redux auth.
  //
  // Initialised from the token synchronously, so a visitor with no token never sees a frame of
  // the loading screen.
  const [checkingHandoff, setCheckingHandoff] = React.useState(
    () => Boolean(readHandoffToken())
  );

  React.useEffect(() => {
    const token = readHandoffToken();
    if (!token) {
      setCheckingHandoff(false);
      return;
    }

    const esChatCookie = Cookies.get("es_chat");
    if (esChatCookie) {
      dispatch(setIsAuthenticating(true));
    }

    redirectIfMigrated(token).then((redirecting) => {
      // Leave the loading screen up while the browser navigates away. Clearing it would flash
      // this app's sign-in page at someone who is already on their way out of it.
      if (redirecting) return;
      setCheckingHandoff(false);
      if (esChatCookie) {
        dispatch(verifyAuthWithEsChat());
      }
    }).catch(() => {
      // `redirectIfMigrated` is documented as never rejecting, but nothing enforces that, and a
      // future edit could break the contract. Without this guard a rejection here would leave
      // `checkingHandoff` stuck true forever, with no sign-in page to fall back to: total loss
      // of the app for anyone holding an `es_chat` cookie. Cheap insurance against that.
      setCheckingHandoff(false);
    });
  }, [dispatch]);

  if (checkingHandoff) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-default-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="text-body font-body text-subtext-color">
            Verifying your session...
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      <GlobalNotification />
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Messenger />
              </ProtectedRoute>
            }
          />

          <Route
            path="/sign-in"
            element={
              <PublicRoute>
                <SignIn />
              </PublicRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </>
  );
}

export default App;
